import React, {
  useState,
  ReactElement,
  useEffect,
  useMemo,
  useCallback,
  useReducer,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Entity } from '../../types/entities/Entity';
import { Schema } from '../../types/Schema';
import { Stack } from '@mui/material';
import { useStateContext } from '../../hooks/useStateContext';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import api from '../../services/api';
import moment from 'moment';
import 'react-tabs/style/react-tabs.css';
import Box from '@mui/material/Box';
import { SlidePaneSkeleton } from '../Loading/SlidePaneSkeleton';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { DailyTransferSchedule } from '../ActivityReservations/DailyTransferSchedule';

export type SlidingPaneAddNewHandle = {
  getState: () => any;
};

type Props<T extends Entity> = {
  schema: Schema<T> | Schema<any>;
  mainSchema?: Schema<T> | Schema<any>;
  id: number | undefined | string;
  onSubmit: (item: Partial<T>) => void;
  result?: (item: Partial<T>) => void;
  onClose: (item: any) => void;
  properties?: Partial<T>;
  propertiesFromTable: any;
  row?: any;
  hasSlidingPane?: boolean;
  stepper?: boolean;
};

const reducer = (state: any, action: any, schema?: any) => {
  switch (action.type) {
    case 'SET_INPUT':
      const newCurrent = {
        ...state.current,
        [action.payload.name]: action.payload.value,
      };
      const newHistory = [
        ...state.history.slice(0, state.pointer + 1),
        newCurrent,
      ];

      if (action.payload.name === 'unit_price') {
        newCurrent['unit_price_gross'] = parseFloat(
          (action.payload.value * (state.current['vat'] / 100 + 1)).toFixed(2)
        );
      }
      if (action.payload.name === 'unit_price_gross') {
        newCurrent['unit_price'] = parseFloat(
          (action.payload.value / (1 + state.current['vat'] / 100)).toFixed(2)
        );
      }

      if (action.payload.name === 'vat') {
        if (newCurrent?.unit_price) {
          newCurrent['unit_price_gross'] = parseFloat(
            (newCurrent?.unit_price * (action.payload.value / 100 + 1)).toFixed(
              2
            )
          );
        }
        if (newCurrent?.unit_price_gross) {
          newCurrent['unit_price'] = parseFloat(
            (
              newCurrent?.unit_price_gross /
              (1 + action.payload.value / 100)
            ).toFixed(2)
          );
        }
      }

      if (action.payload.name === 'group_price' && action.payload.split) {
        newCurrent[`prices`] = {};
        for (let i = 1; i < 51; i++) {
          const price = parseFloat((action.payload?.value / i).toFixed(2));
          newCurrent[`prices`][`size_${i}`] = price;
        }
      }

      if (action.payload.name === 'total_price') {
        const dep = state.current.departure;
        const arr = state.current.arrival;
        const size = state.current.size;

        if (dep && arr && size) {
          const depDate = new Date(dep);
          const arrDate = new Date(arr);

          const differenceInTime = depDate.getTime() - arrDate.getTime();
          const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

          const nights = Math.max(1, differenceInDays);

          newCurrent['sales_price_person_night'] =
            action.payload.value / nights / size;
        }
      }

      if (action.payload.name === 'sales_price_person_night') {
        const dep = state.current.departure;
        const arr = state.current.arrival;
        const size = state.current.size;

        if (dep && arr && size) {
          const depDate = new Date(dep);
          const arrDate = new Date(arr);

          const differenceInTime = depDate.getTime() - arrDate.getTime();
          const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

          const nights = Math.max(1, differenceInDays);

          newCurrent['total_price'] = action.payload.value * nights * size;
        }
      }

      if (action.payload.name === 'total_cost') {
        const dep = state.current.departure;
        const arr = state.current.arrival;
        const size = state.current.size;

        if (dep && arr && size) {
          const depDate = new Date(dep);
          const arrDate = new Date(arr);

          const differenceInTime = depDate.getTime() - arrDate.getTime();
          const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

          const nights = Math.max(1, differenceInDays);

          newCurrent['gross_cost_person_night'] =
            action.payload.value / nights / size;
        }
      }

      if (action.payload.name === 'gross_cost_person_night') {
        const dep = state.current.departure;
        const arr = state.current.arrival;
        const size = state.current.size;

        if (dep && arr && size) {
          const depDate = new Date(dep);
          const arrDate = new Date(arr);

          const differenceInTime = depDate.getTime() - arrDate.getTime();
          const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

          const nights = Math.max(1, differenceInDays);

          newCurrent['total_cost'] = action.payload.value * nights * size;
        }
      }
      if (
        action?.schema &&
        (action?.schema?.name === 'Services' ||
          action?.schema?.name === 'Activity') &&
        action.payload.name === 'name'
      ) {
        newCurrent['sp_name'] = action.payload.value;
      }

      return {
        ...state,
        history: newHistory,
        current: newCurrent,
        pointer: state.pointer + 1,
        lastChanged: action.payload.name,
      };
    case 'SET_DEFAULT':
      const newCurrentDefault = action.payload ? action.payload : {};

      const newHistoryDefault = [...state.history, newCurrentDefault];
      return {
        ...state,
        history: newHistoryDefault,
        current: newCurrentDefault,
        lastChanged: undefined,
      };
    case 'UNDO':
      if (state.pointer > 0) {
        const prevPointer = state.pointer - 1;
        return {
          ...state,
          current: state.history[prevPointer],
          pointer: prevPointer,
        };
      }
      return state;
    case 'REDO':
      if (state.pointer < state.history.length - 1) {
        const nextPointer = state.pointer + 1;
        return {
          ...state,
          current: state.history[nextPointer],
          pointer: nextPointer,
        };
      }
      return state;
    default:
      return state;
  }
};

export const SlidingPaneAddNew = forwardRef(
  <T extends Entity>(
    {
      schema,
      mainSchema,
      onSubmit: handleSubmit,
      id,
      result,
      propertiesFromTable,
      row,
      onClose,
      hasSlidingPane,
      stepper,
    }: Props<T>,
    ref: React.Ref<SlidingPaneAddNewHandle>
  ) => {
    const [loading, setLoading] = useState(true);
    const [softLoading, setSoftLoading] = useState(false);
    const [edited, setEdited] = useState(false);
    const { user } = useStateContext();
    const [
      requiredFieldsFromDatabase,
      setRequiredFieldsFromDatabase,
    ] = useState<any>([]);
    const [state, dispatch] = useReducer(reducer, {
      history: [],
      current: {},
      pointer: -1,
    });

    useImperativeHandle(
      ref,
      () => ({
        getState: () => state.current, // ✅ ezt húzod ki gombnyomásra
      }),
      [state.current]
    );

    const [missingFields, setMissingFields] = useState<any>([]);
    const [confirmPriceSplit, setConfirmPriceSplit] = useState({
      open: false,
      data: {},
    });
    const [forcedValue, setForcedValue] = useState<any>({});
    const draggablePaneId = useMemo(() => {
      return `draggable-responsive-add-new-dialog-${uuidv4()}`;
    }, []);

    useEffect(() => {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }, []);

    const getMissingFields = useCallback(async () => {
      const requiredFieldFromView = schema?.viewTable
        ? await api
            .get(`/required-fields/${user.id}/${schema?.viewTable}`)
            .then((res: any) => {
              setRequiredFieldsFromDatabase(res);
              return res;
            })
        : [];

      const columnKeys: any = schema?.columns
        ? (Object.keys(schema.columns) as Array<keyof T>)
        : [];

      const requiredFields = columnKeys.filter((columnKey: string) => {
        const column: any = schema.columns[columnKey as keyof {}];

        const required = requiredFieldFromView[`${columnKey}`];

        if (schema?.viewTable) {
          if (column.required && !column.readOnly) {
            return true;
          }
          return required;
        }

        return column.required && !column.readOnly;
      });

      return requiredFields.filter(
        (requiredField: any) =>
          state.current[requiredField] === undefined ||
          state.current[requiredField] === null ||
          state.current[requiredField] === ''
      );
    }, [state]);

    const PaperComponent = useCallback((props: PaperProps) => {
      return (
        <Draggable
          handle={`#${draggablePaneId}`}
          cancel={'[class*="MuiDialogContent-root"], .non-draggable-button'}
        >
          <Paper {...props} />
        </Draggable>
      );
    }, []);

    const onSubmit = useCallback(async () => {
      setSoftLoading(true);

      try {
        const missingFields: any = await getMissingFields();
        setMissingFields(missingFields);

        if (!missingFields.length) {
          if (schemaHasClient(schema)) {
            const result: any = await schema.client.create({
              ...state.current,
              group: propertiesFromTable?.group || state.current?.group,
              schema: mainSchema,
            });

            toast.success(
              `Successfully added ${schema.name} ID: ${result?.id} Name: ${result?.name}`
            );

            if (
              schema?.pluralName.toLowerCase() === 'activities' &&
              state.current['images' as keyof {}]
            ) {
              const files: any = state.current['images' as keyof {}];
              const formData = new FormData();
              const salesPartnerRoles: any = [];
              const extraData: any = [];

              files.forEach((file: any, i: number) => {
                salesPartnerRoles.push({
                  sales_partner_visible: file?.sales_partner_visible,
                  sales_partner_use: file?.sales_partner_use,
                });
                extraData.push({
                  title: file?.title,
                  caption: file?.caption,
                  description: file?.description,
                });
                formData.append(`file-${i}`, file);
              });

              formData.append('column', 'images');
              formData.append('id', result?.id);
              formData.append('schema', JSON.stringify(schema));
              formData.append(
                'salesPartnerRoles',
                JSON.stringify(salesPartnerRoles)
              );
              formData.append('extraData', JSON.stringify(extraData));

              try {
                await api.postFile(`/upload-files`, formData);
              } catch (error) {
                console.error('File upload error:', error);
              }
            }

            setEdited(false);
            handleSubmit(result);
          }
        } else {
          toast.error('Required field is missing');
        }
      } catch (error: any) {
        let array: string[] = [];
        for (let errorKey in error) {
          for (let errorMsg in error[errorKey]) {
            array.push(`${error[errorKey][errorMsg]}`);
          }
        }
        toast.error(array.join(','));
      } finally {
        setSoftLoading(false);
      }
    }, [getMissingFields, onClose, schema, state]);

    function handleSetChanges(name: any, value: any) {
      if (name === 'group_price') {
        setConfirmPriceSplit({
          open: true,
          data: value,
        });
      } else {
        dispatch({
          type: 'SET_INPUT',
          payload: { name, value },
          schema: schema,
        });
      }
    }

    useEffect(() => {
      setLoading(true);
      const searchParams = new URLSearchParams();

      const keys = Object.keys(schema.columns).filter((column: any) => {
        if (
          column === 'a_status' ||
          column === 'b_status' ||
          column === 's_status'
        ) {
          return false;
        }
        return column.includes('status');
      });

      searchParams.append('statusFields', JSON.stringify(keys));

      if (
        schema.viewTable === "activity_reservations" &&
        Object.keys(propertiesFromTable).length
      ) {
        if (propertiesFromTable?.group) {
          api
            .get(`/groups/${propertiesFromTable?.group?.id}`, searchParams)
            .then((result: any) => {
              dispatch({
                type: 'SET_DEFAULT',
                payload: {
                  ['group_size']: result?.size,
                  ['type']: {
                    id: 1,
                    name: 'Booked'
                },
                  ['group']: {
                    id: result?.id,
                    name: result?.name,
                  },
                  ['sales_partner']: result?.sales_partner
                    ? result?.sales_partner
                    : undefined,
                  ['date_added']: moment(new Date()).format('YYYY-MM-DD HH:mm'),
                  ['date']: moment(new Date(result?.arrival_date)).format(
                    'YYYY-MM-DD HH:mm'
                  ),
                },
              });
              setLoading(false);
            });
        } else {
          dispatch({
            type: 'SET_DEFAULT',
            payload: {
              ['date_added']: moment(new Date()).format('YYYY-MM-DD HH:mm'),
            },
          });
          setLoading(false);
        }
      } else {
        setLoading(false);
        dispatch({
          type: 'SET_DEFAULT',
          payload: {
            ['selling_price_currency']: {
              id: 1,
              name: 'EUR',
            },
            ['cost_currency']: {
              id: 1,
              name: 'EUR',
            },
            ['base_currency']: {
              id: 1,
              name: 'EUR',
            },
            ['price_currency']: {
              id: 1,
              name: 'EUR',
            },
            ['tourist_tax_currency']: {
              id: 1,
              name: 'EUR',
            },
            ['currency']: {
              id: 1,
              name: 'EUR',
            },
            ['guide_wage_currency']: {
              id: 2,
              name: 'HUF',
            },
          },
        });
      }
    }, []);

    useEffect(() => {
      if (schema?.viewTable) {
        api
          .get(`/required-fields/${user.id}/${schema?.viewTable}`)
          .then((res: any) => {
            setRequiredFieldsFromDatabase(res);
          });
      }
    }, []);

    useEffect(() => {
      if (!edited && state.pointer > -1) {
        setEdited(true);
      }
      if (state.lastChanged === 'group_price' && confirmPriceSplit.open) {
        setForcedValue({ group_price: state.current.group_price });
        setConfirmPriceSplit({
          open: false,
          data: {},
        });
      }
    }, [state]);

    const today = new Date().toISOString().split('T')[0];

    return (
      <>
        <Stack direction={'row'} gap={2}>
          <Stack
            sx={{
              overflowY: 'auto',
              maxHeight: {
                md: 'calc(100vh - 200px)',
              },
              width: '50%',

              background: '#fff',
              borderRadius: '10px',
              boxShadow: '0px 0px 4px 5px #0000000f',
            }}
          >
            {loading ? (
              <SlidePaneSkeleton />
            ) : (
              <>
                {(Object.keys(schema.columns) as Array<keyof T>).map(
                  (column) => {
                    const tabName = schema.columns[column]?.tab_name
                      ? schema.columns[column]?.tab_name
                      : 'general';

                    const withoutNameColumn =
                      schema.columns[column]?.withoutNameColumn;

                    if ('general' !== tabName) {
                      return <></>;
                    }

                    const {
                      readOnly,
                      name,
                      fieldRenderer: Field,
                      slidePaneFieldRenderer: FieldForPane,
                      slidePaneFieldRendererAddNew: FieldForPaneAddNew,
                      accordingTo,
                      nameForSidePane,
                      hide,
                      required,
                      fieldType,
                    } = schema.columns[column];
                    const isMissing = missingFields.includes(column);

                    const key = accordingTo as keyof T;
                    let accordingToHide = false;
                    if (typeof accordingTo !== 'undefined') {
                      accordingToHide = true;
                    }
                    let accordingToSendData: any = undefined;

                    if (
                      accordingToHide &&
                      schema?.name === 'Service Reservations View' &&
                      column === 'service_provider'
                    ) {
                      accordingToHide = false;
                    }

                    let readOnlyTemp = readOnly;
                    if (
                      schema?.name === 'Activity Reservation' &&
                      column === 'group'
                    ) {
                      readOnlyTemp = false;
                    }

                    if (
                      state.current[key] !== undefined &&
                      state.current[key] !== null &&
                      state.current[key] !== ''
                    ) {
                      accordingToHide = false;
                      const accordingToData: any = state.current[key];
                      accordingToSendData = {
                        name: key,
                        id: accordingToData?.id,
                        value: state.current[key],
                      };
                    }

                    if (
                      accordingToHide &&
                      schema?.viewTable === 'todos' &&
                      column === 'assigned_persons'
                    ) {
                      if (state.current?.private === true) {
                        accordingToHide = true;
                      }
                    }

                    return (
                      <>
                        <Stack
                          key={column.toString()}
                          direction="row"
                          spacing={0}
                          sx={{
                            width: '100%',
                            display: hide ? 'none' : '',
                          }}
                        >
                          {!withoutNameColumn && (
                            <Box
                              sx={{
                                flex: 1,
                                borderBottom: '1px solid #dfdbdb',
                                borderRight: '1px solid #dfdbdb',
                                borderLeft: '1px solid #dfdbdb',
                                display: 'flex',
                                alignItems: 'center',
                                background: '#f1f1f1',
                              }}
                            >
                              <span className={'px-[8px]'}>{`${
                                nameForSidePane ? nameForSidePane : name
                              } ${
                                requiredFieldsFromDatabase[column] || required
                                  ? '*'
                                  : ''
                              }`}</span>
                              {isMissing && (
                                <span style={{ color: 'red' }}>
                                  This field is missing
                                </span>
                              )}
                            </Box>
                          )}

                          <Box
                            sx={{
                              flex: 2,
                              borderBottom: '1px solid #dfdbdb',
                              borderRight: '1px solid #dfdbdb',
                              '& .MuiFormControl-root.MuiTextField-root': {
                                width: '100%',
                              },
                              '& .DraftEditor-root': {
                                padding: '10px',
                                width: '100%',
                              },
                              maxWidth: withoutNameColumn
                                ? '100%'
                                : 'calc(100% - 204px)',
                            }}
                          >
                            {FieldForPaneAddNew ? (
                              <>
                                <FieldForPaneAddNew
                                  onChange={(value: any) => {
                                    handleSetChanges(column, value);
                                  }}
                                  defaultValue={state.current[column]}
                                  accordingToValue={accordingToSendData}
                                  isActive={!accordingToHide && !readOnlyTemp}
                                  disabled={readOnlyTemp || accordingToHide}
                                  intent={isMissing ? 'danger' : undefined}
                                  addingNewEntity={true}
                                  mui={true}
                                  column={schema.columns[column]}
                                  isAddingNew={true}
                                  allValue={state.current}
                                  mainSchema={schema}
                                  forcedValue={forcedValue[column]}
                                />
                              </>
                            ) : (
                              <>
                                {FieldForPane ? (
                                  <FieldForPane
                                    onChange={(value: any) => {
                                      handleSetChanges(column, value);
                                    }}
                                    defaultValue={state.current[column]}
                                    accordingToValue={accordingToSendData}
                                    isActive={!accordingToHide && !readOnlyTemp}
                                    disabled={readOnlyTemp || accordingToHide}
                                    intent={isMissing ? 'danger' : undefined}
                                    addingNewEntity={true}
                                    mui={true}
                                    column={schema.columns[column]}
                                    isAddingNew={true}
                                    allValue={state.current}
                                    mainSchema={schema}
                                    forcedValue={forcedValue[column]}
                                  />
                                ) : (
                                  <></>
                                )}
                              </>
                            )}
                          </Box>
                        </Stack>
                      </>
                    );
                  }
                )}
              </>
            )}
          </Stack>
          {schema.viewTable === 'activity_reservations' && (
            <Box
              sx={{
                width: '50%',
                background: '#fff',
                borderRadius: '10px',
                boxShadow: '0px 0px 4px 5px #0000000f',
                overflow: 'hidden',
              }}
            >
              <DailyTransferSchedule
                date={state.current?.date ?? today}
                minimal={true}
              />
            </Box>
          )}
        </Stack>
      </>
    );
  }
);

function schemaHasClient<T extends Entity>(
  schema: Schema<T>
): schema is SchemaWithClient<T> {
  return 'client' in schema;
}
